import { useMemo, useState } from 'react';
import { CheckCircle2, ChevronRight, Eye, EyeOff, Hash, KeyRound, MailCheck, ShieldCheck } from 'lucide-react';
import './ForgotPasswordModal.css';

const ForgotPasswordModal = ({ forgotPassword, onClose }) => {
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const stepNumber = useMemo(() => {
    if (forgotPassword.step === 'idle') return 1;
    if (forgotPassword.step === 'requested') return 2;
    if (forgotPassword.step === 'verified') return 3;
    return 3;
  }, [forgotPassword.step]);

  const stepItems = [
    { key: 'idle', label: 'Request code', icon: KeyRound },
    { key: 'requested', label: 'Verify OTP', icon: MailCheck },
    { key: 'verified', label: 'Set password', icon: ShieldCheck }
  ];

  return (
    <div className="modal-overlay forgot-password-modal-overlay" onClick={onClose}>
      <div className="modal-content forgot-password-modal-content" onClick={(event) => event.stopPropagation()}>
        <div className="forgot-password-modal-header">
          <div className="forgot-password-modal-copy">
            <span className="forgot-password-kicker">Account recovery</span>
            <h1 className="portal-title">Reset your password</h1>
            <p className="portal-subtitle">
              Verify your store access in three quick steps and set a new password securely.
            </p>
          </div>
          <span className="forgot-password-step-chip">Step {stepNumber} of 3</span>
        </div>

        <div className="forgot-password-progress">
          {stepItems.map(({ key, label, icon: Icon }, index) => {
            const isActive = stepItems[stepNumber - 1]?.key === key;
            const isComplete = index < stepNumber - 1;
            return (
              <div key={key} className={`forgot-password-progress-item${isActive ? ' active' : ''}${isComplete ? ' complete' : ''}`}>
                <span className="forgot-password-progress-icon" aria-hidden="true">
                  {isComplete ? <CheckCircle2 size={16} /> : <Icon size={16} />}
                </span>
                <span>{label}</span>
              </div>
            );
          })}
        </div>

        {forgotPassword.step === 'idle' && (
          <div className="form-grid forgot-password-form-grid">
            <div className="forgot-password-callout">
              <strong>Use your store code</strong>
              <span>We’ll send a one-time verification code to the registered email for this store.</span>
            </div>
            <div className="input-group">
              <label className="input-label">Store Code</label>
              <div className="forgot-password-input-shell">
                <span className="forgot-password-input-icon" aria-hidden="true"><Hash size={16} /></span>
                <input
                  className="input forgot-password-input"
                  value={forgotPassword.email}
                  maxLength={50}
                  placeholder="e.g. ZOPEO-PHOENIX-01"
                  onChange={(event) => forgotPassword.setEmail(event.target.value)}
                />
              </div>
            </div>
          </div>
        )}

        {forgotPassword.step === 'requested' && (
          <div className="form-grid forgot-password-form-grid">
            <div className="forgot-password-callout">
              <strong>Check your email</strong>
              <span>{forgotPassword.maskedEmail ? `We sent a 6-digit code to ${forgotPassword.maskedEmail}.` : 'Enter the OTP you received to continue.'}</span>
            </div>
            <div className="input-group">
              <label className="input-label">Store Code</label>
              <div className="forgot-password-input-shell">
                <span className="forgot-password-input-icon" aria-hidden="true"><Hash size={16} /></span>
                <input className="input forgot-password-input" value={forgotPassword.email} disabled />
              </div>
            </div>
            <div className="input-group">
              <label className="input-label">OTP</label>
              <div className="forgot-password-input-shell">
                <span className="forgot-password-input-icon" aria-hidden="true"><MailCheck size={16} /></span>
                <input
                  className="input forgot-password-input forgot-password-otp-input"
                  value={forgotPassword.otp}
                  maxLength={6}
                  placeholder="Enter 6-digit OTP"
                  onChange={(event) => forgotPassword.setOtp(event.target.value.replace(/\D/g, '').slice(0, 6))}
                />
              </div>
            </div>
          </div>
        )}

        {forgotPassword.step === 'verified' && (
          <div className="form-grid forgot-password-form-grid">
            <div className="forgot-password-callout success">
              <strong>OTP verified</strong>
              <span>Create a strong new password for this store account.</span>
            </div>
            <div className="input-group">
              <label className="input-label">New Password</label>
              <div className="store-code-wrapper forgot-password-input-shell forgot-password-input-shell--action">
                <span className="forgot-password-input-icon" aria-hidden="true"><ShieldCheck size={16} /></span>
                <input
                  className="input forgot-password-input"
                  type={showNewPassword ? 'text' : 'password'}
                  value={forgotPassword.newPassword}
                  maxLength={128}
                  placeholder="Enter your new password"
                  onChange={(event) => forgotPassword.setNewPassword(event.target.value)}
                />
                <button
                  type="button"
                  className="copy-btn forgot-password-input-action"
                  onClick={() => setShowNewPassword((prev) => !prev)}
                >
                  {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div className="input-group">
              <label className="input-label">Confirm New Password</label>
              <div className="store-code-wrapper forgot-password-input-shell forgot-password-input-shell--action">
                <span className="forgot-password-input-icon" aria-hidden="true"><ShieldCheck size={16} /></span>
                <input
                  className="input forgot-password-input"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={forgotPassword.confirmPassword}
                  maxLength={128}
                  placeholder="Confirm your new password"
                  onChange={(event) => forgotPassword.setConfirmPassword(event.target.value)}
                />
                <button
                  type="button"
                  className="copy-btn forgot-password-input-action"
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div className="forgot-password-password-rules">
              <span>Minimum 8 characters</span>
              <span>At least 1 capital letter</span>
              <span>Must include letters and numbers</span>
            </div>
          </div>
        )}

        <div className="modal-actions forgot-password-modal-actions">
          <button type="button" className="btn ghost" onClick={onClose}>
            Cancel
          </button>

          {forgotPassword.step === 'idle' && (
            <button
              type="button"
              className="btn primary forgot-password-primary-action"
              onClick={forgotPassword.requestOtp}
              disabled={forgotPassword.loading}
            >
              {forgotPassword.loading ? 'Sending...' : <>Send OTP <ChevronRight size={16} /></>}
            </button>
          )}

          {forgotPassword.step === 'requested' && (
            <button
              type="button"
              className="btn primary forgot-password-primary-action"
              onClick={forgotPassword.verifyOtp}
              disabled={forgotPassword.loading}
            >
              {forgotPassword.loading ? 'Verifying...' : <>Verify OTP <ChevronRight size={16} /></>}
            </button>
          )}

          {forgotPassword.step === 'verified' && (
            <button
              type="button"
              className="btn primary forgot-password-primary-action"
              onClick={async () => {
                const success = await forgotPassword.resetPassword();
                if (success) {
                  onClose();
                }
              }}
              disabled={forgotPassword.loading}
            >
              {forgotPassword.loading ? 'Resetting...' : <>Reset password <ChevronRight size={16} /></>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordModal;
